import { getProvider } from "./providers";

/**
 * Reusable "full activation" of an inbound (write_in) sync for AI-memory style
 * providers (Gmail emails, Google Calendar meetings).
 *
 * This mirrors the working manual recipe in
 * `ProviderNewFlow.handleActivateSync(slug)` so that connecting an integration
 * activates the first-sync automatically instead of requiring the user to open
 * Settings → Integrations and click "Start sync".
 *
 * Steps (idempotent, best-effort — never throws):
 *   1. Ensure a `write_in` SyncConfiguration exists (reuse an existing one for
 *      this integration + destination object, otherwise create it disabled).
 *   2. Enable that SyncConfiguration (`status: 'enabled'`).
 *   3. Ensure + patch BOTH flows (first-sync + receive) with `{ sync_id }`.
 *   4. Run the first-sync flow to kick off the initial backfill.
 *
 * Hooks cannot run outside React components, so the sync-config create/update
 * callables and the list of existing sync-configs are passed in by the caller.
 */

type SyncConfigLike = {
  id?: string;
  integration_id?: string;
  sync_direction?: string;
  destination_object?: string;
  [key: string]: unknown;
};

export interface ActivateInboundSyncParams {
  integrationApp: any;
  workspaceId?: string;
  integrationId?: string;
  providerKey: string;
  /** AI-memory slug: 'conversation_messages' (gmail emails) | 'meetings' (calendar). */
  slug: string;
  /** object_definition id for the slug (fetched fresh after connect). */
  destinationObjectId?: string;
  /** Source collection on the provider (e.g. 'emails' / 'events'). */
  sourceObject?: string;
  createSyncConfig: (args: { workspace_id: string; data: any }) => Promise<any>;
  updateSyncConfig: (args: { workspace_id: string; sync_id: string; data: any }) => Promise<any>;
  existingSyncConfigs?: SyncConfigLike[] | { data?: SyncConfigLike[] } | null;
}

// users <-> contacts is the only canonicalization used by sync configs.
const canonDestination = (slug: string) => (slug === "contacts" ? "users" : slug);
const canonSlug = (slug: string) => (slug === "users" ? "contacts" : slug);

const normalizeConfigs = (
  configs: SyncConfigLike[] | { data?: SyncConfigLike[] } | null | undefined,
): SyncConfigLike[] => {
  if (!configs) return [];
  if (Array.isArray(configs)) return configs;
  if (Array.isArray(configs.data)) return configs.data;
  return [];
};

/**
 * Returns `true` when the full activation succeeded, `false` when a prerequisite
 * was missing or activation failed (so callers can fall back to a best-effort
 * flow trigger).
 */
export const activateInboundSyncForProvider = async (
  params: ActivateInboundSyncParams,
): Promise<boolean> => {
  const {
    integrationApp,
    workspaceId,
    integrationId,
    providerKey,
    slug,
    destinationObjectId,
    sourceObject,
    createSyncConfig,
    updateSyncConfig,
    existingSyncConfigs,
  } = params;

  try {
    if (!integrationApp || !workspaceId || !integrationId || !destinationObjectId) {
      console.warn("[activateInboundSync] missing prerequisites; skipping full activation", {
        hasIntegrationApp: !!integrationApp,
        workspaceId,
        integrationId,
        destinationObjectId,
        slug,
      });
      return false;
    }

    const provider = getProvider(providerKey);
    const firstSyncMap = (provider.flowKeys.firstSync || {}) as Record<string, string>;
    const receiveMap = (provider.flowKeys.receive || {}) as Record<string, string>;
    // Some providers (google-calendar) store the meetings flow under the
    // `conversation_messages` key, so fall back to it when the slug is missing.
    const firstFlowKey = firstSyncMap[slug] || firstSyncMap.conversation_messages;
    const receiveFlowKey = receiveMap[slug] || receiveMap.conversation_messages;
    if (!firstFlowKey || !receiveFlowKey) {
      console.warn("[activateInboundSync] could not resolve flow keys; skipping", {
        providerKey,
        slug,
        firstFlowKey,
        receiveFlowKey,
      });
      return false;
    }

    const effectiveSource = sourceObject || provider.defaultSourceCollections?.[slug as never] || slug;

    // 1. Ensure a write_in sync configuration (reuse existing if present).
    const configs = normalizeConfigs(existingSyncConfigs);
    const existing = configs.find(
      (config) =>
        config &&
        config.integration_id === integrationId &&
        config.sync_direction === "write_in" &&
        canonSlug(String(config.destination_object)) === canonSlug(slug),
    );

    let syncId = existing?.id;
    if (!syncId) {
      const data = {
        workspace_id: workspaceId,
        integration_id: integrationId,
        source_object: effectiveSource,
        source_object_id: effectiveSource,
        destination_object: canonDestination(slug),
        destination_object_id: destinationObjectId,
        sync_direction: "write_in",
        status: "disabled",
        matcher_field_name: "external_id",
        source_matcher_field: "external_id",
        destination_matcher_field: "external_id",
        allow_creates: true,
        allow_updates: true,
      };
      const created = await createSyncConfig({ workspace_id: workspaceId, data });
      const createdSync = created?.data ?? created;
      syncId = createdSync?.id;
    }

    if (!syncId) {
      console.warn("[activateInboundSync] could not resolve sync_id; skipping flow activation", { slug });
      return false;
    }

    // 2. Enable the sync configuration.
    await updateSyncConfig({ workspace_id: workspaceId, sync_id: syncId, data: { status: "enabled" } });

    // 3. Ensure both flows exist, then patch them with the sync_id.
    await Promise.all([
      integrationApp.connection(providerKey).flow(firstFlowKey).get({ autoCreate: true }),
      integrationApp.connection(providerKey).flow(receiveFlowKey).get({ autoCreate: true }),
    ]);
    await Promise.all([
      integrationApp.connection(providerKey).flow(firstFlowKey).patch({ parameters: { sync_id: syncId }, enabled: true }),
      integrationApp.connection(providerKey).flow(receiveFlowKey).patch({ parameters: { sync_id: syncId }, enabled: true }),
    ]);

    // 4. Trigger the initial full sync run.
    await integrationApp.connection(providerKey).flow(firstFlowKey).run({ input: null });

    return true;
  } catch (error) {
    // Best-effort only — never block the connect UX.
    console.warn("[activateInboundSync] best-effort activation failed", error);
    return false;
  }
};

export default activateInboundSyncForProvider;
